import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Plus, Edit, Trash2, Users, DollarSign, AlertCircle, Loader2, RefreshCw, ArrowRight, Upload, Share2, X, Mail, Shield, FileCheck, Archive } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { DatabaseService } from '../lib/database';
import { useDeal } from '../contexts/DealContext';
import DealDeletionModal from './DealDeletionModal';
import type { UserDeal, CreateDealRequest, DealAccess } from '../types/database';
import { getDealStatusLabel, getDealStatusColor, getDealAccessTypeLabel, getDealAccessTypeColor, formatCurrency } from '../types/database';

export function DealManager() {
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectDeal, refreshDeals } = useDeal();
  const [deals, setDeals] = useState<UserDeal[]>([]);
  const [ndaDeals, setNdaDeals] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDeals, setIsLoadingDeals] = useState(false); // Guard to prevent multiple simultaneous calls
  const [message, setMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<string | null>(null);
  const [dealAccess, setDealAccess] = useState<DealAccess[]>([]);
  const [isLoadingAccess, setIsLoadingAccess] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ dealId: string; dealBusinessName: string } | null>(null);
  const [deletionModal, setDeletionModal] = useState<{ deal: UserDeal } | null>(null);
  const [editingDeal, setEditingDeal] = useState<UserDeal | null>(null);
  const [editDeal, setEditDeal] = useState<CreateDealRequest>({
    business_name: '',
    description: '',
    deal_value: undefined,
    currency: 'USD'
  });
  
  // Share deal state
  const [shareModal, setShareModal] = useState<{ deal: UserDeal; email: string; isLoading: boolean } | null>(null);
  
  // NDA state
  const [ndaModal, setNdaModal] = useState<{ deal: UserDeal; email: string; isLoading: boolean } | null>(null);
  
  // Check if we're in create mode
  const isCreateMode = location.pathname === '/deals/create';

  const [newDeal, setNewDeal] = useState<CreateDealRequest>({
    business_name: '',
    description: '',
    deal_value: undefined,
    currency: 'USD'
  });

  // Load user's deals and NDA invitations - only once when component mounts
  useEffect(() => {
    if (user?.emailAddresses?.[0]?.emailAddress) {
      loadDeals();
      
      // Check if user just completed NDA signing
      const ndaSigningInProgress = localStorage.getItem('nda_signing_in_progress');
      if (ndaSigningInProgress === 'true') {
        localStorage.removeItem('nda_signing_in_progress');
        localStorage.removeItem('nda_deal_id');
        
        // Trigger a refresh after a short delay to ensure backend has processed the NDA
        setTimeout(() => {
          loadDeals();
        }, 1000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Only depend on user ID, not the entire user object

  // Only listen for storage events (when NDA is signed in another tab) - remove other triggers
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'nda_signed' || e.key === 'deal_updated') {
        loadDeals();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const loadDeals = async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingDeals) {
      return;
    }
    
    setIsLoadingDeals(true);
    setIsLoading(true);
    try {
      // Load all deals (including pending NDAs) from the database function
      // Pass null to get ALL deals the user has access to (read, admin, or creator)
      const response = await DatabaseService.getUserDeals(null, user?.id);
      if (response.data) {
        // Separate pending NDAs from regular deals
        // Pending NDAs have is_pending_nda = true and should be displayed in the NDA section
        const regularDeals = response.data.filter(d => !d.is_pending_nda);
        const pendingNdaDeals = response.data.filter(d => d.is_pending_nda);
        
        console.log('[DEBUG - DELETE WHEN FINISHED] All deals from DB:', response.data); // [DEBUG - DELETE WHEN FINISHED]
        console.log('[DEBUG - DELETE WHEN FINISHED] Regular deals (is_pending_nda=false):', regularDeals); // [DEBUG - DELETE WHEN FINISHED]
        console.log('[DEBUG - DELETE WHEN FINISHED] Pending NDA deals (is_pending_nda=true):', pendingNdaDeals); // [DEBUG - DELETE WHEN FINISHED]
        
        setDeals(regularDeals);
        
        // Transform pending NDA deals to match the expected format
        const ndaList = pendingNdaDeals.map(deal => ({
          deal_id: deal.deal_id,
          invitation_id: deal.invitation_id,
          inviter_name: deal.inviter_name,
          inviter_email: deal.inviter_email,
          public_business_alias: deal.business_alias,
          deal_title: deal.business_alias
        }));
        
        console.log('[DEBUG - DELETE WHEN FINISHED] Transformed NDA list:', ndaList); // [DEBUG - DELETE WHEN FINISHED]
        
        setNdaDeals(ndaList);
      } else if (response.error) {
        setMessage(`Error loading deals: ${response.error}`);
      }
    } catch (error) {
      console.error('DealManager.loadDeals: Exception', error);
      setMessage(`Error loading deals: ${error}`);
    } finally {
      setIsLoading(false);
      setIsLoadingDeals(false);
    }
  };

  // Handle deal selection and navigation
  const handleDealSelect = (deal: UserDeal) => {
    selectDeal(deal);
    navigate(`/deals/${deal.deal_id}/dashboard`);
  };

  // Handle NDA signing
  const handleSignNda = (ndaDeal: any) => {
    console.log('[DEBUG - DELETE WHEN FINISHED] NDA Deal object:', ndaDeal); // [DEBUG - DELETE WHEN FINISHED]
    console.log('[DEBUG - DELETE WHEN FINISHED] Deal title for NDA:', ndaDeal.deal_title || ndaDeal.public_business_alias); // [DEBUG - DELETE WHEN FINISHED]
    
    const ndaUrl = `/nda?deal_id=${ndaDeal.deal_id}&email=${user?.emailAddresses?.[0]?.emailAddress}&recipient=true&invitation_id=${ndaDeal.invitation_id}&deal_title=${encodeURIComponent(ndaDeal.deal_title || ndaDeal.public_business_alias || 'Business Opportunity')}`;
    
    console.log('[DEBUG - DELETE WHEN FINISHED] NDA URL:', ndaUrl); // [DEBUG - DELETE WHEN FINISHED]
    
    // Set a flag in localStorage to trigger refresh when user returns
    localStorage.setItem('nda_signing_in_progress', 'true');
    localStorage.setItem('nda_deal_id', ndaDeal.deal_id);
    
    window.location.href = ndaUrl;
  };

  const loadDealAccess = async (dealId: string) => {
    setIsLoadingAccess(true);
    try {
      const response = await DatabaseService.getDealAccess(dealId, user?.id);
      if (response.data) {
        setDealAccess(response.data);
      } else if (response.error) {
        setMessage(`Error loading deal access: ${response.error}`);
      }
    } catch (error) {
      setMessage(`Error loading deal access: ${error}`);
    } finally {
      setIsLoadingAccess(false);
    }
  };

  const handleCreateDeal = async () => {
    if (!newDeal.business_name) {
      setMessage('Please enter a business name');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    if (!user) {
      setMessage('User not authenticated');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setIsLoading(true);
    try {
      // Create deal with business_name as the title
      const dealRequest = {
        business_name: newDeal.business_name,
        description: newDeal.description,
        deal_value: newDeal.deal_value,
        currency: newDeal.currency
      };
      
      const response = await DatabaseService.createDeal(dealRequest, user.id);
      if (response.error) {
        setMessage(`Error creating deal: ${response.error}`);
      } else {
        setMessage('Deal created successfully');
        setShowCreateForm(false);

        // Navigate directly to the proforma page for the created deal
        const createdDealId = response.data?.id;
        await refreshDeals();
        if (createdDealId) {
          navigate(`/deals/${createdDealId}/dashboard`);
        } else {
          // Fallback: refresh and navigate to deals list
          await loadDeals();
          navigate('/deals');
        }
      }
    } catch (error) {
      setMessage(`Error creating deal: ${error}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleSelectDeal = (dealId: string) => {
    setSelectedDeal(dealId);
    loadDealAccess(dealId);
  };


  const handleRevokeAccess = async (dealId: string, userId: string) => {
    try {
      const response = await DatabaseService.revokeDealAccess({
        deal_id: dealId,
        user_id: userId
      });
      
      if (response.error) {
        setMessage(`Error revoking access: ${response.error}`);
      } else {
        setMessage('Access revoked successfully');
        loadDealAccess(dealId);
      }
    } catch (error) {
      setMessage(`Error revoking access: ${error}`);
    } finally {
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleDealDeletion = (deal: UserDeal) => {
    setDeletionModal({ deal });
  };

  const handleArchiveDeal = async (dealId: string, reason?: string) => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const response = await DatabaseService.archiveDeal(dealId, user.id, reason);
      
      if (response.error) {
        setMessage(`Error archiving deal: ${response.error}`);
      } else {
        setMessage('Deal archived successfully');
        // Remove from current deals list
        setDeals(prevDeals => prevDeals.filter(d => d.deal_id !== dealId));
        setNdaDeals(prevDeals => prevDeals.filter(d => d.deal_id !== dealId));
        
        // Clear selection if the archived deal was selected
        if (selectedDeal === dealId) {
          setSelectedDeal(null);
          setDealAccess([]);
        }
      }
    } catch (error) {
      setMessage(`Error archiving deal: ${error}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handlePermanentDelete = async (dealId: string) => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const response = await DatabaseService.deleteDealCompletely(dealId, user.id);
      
      if (response.error) {
        setMessage(`Error deleting deal: ${response.error}`);
      } else {
        setMessage('Deal permanently deleted');
        // Remove from current deals list
        setDeals(prevDeals => prevDeals.filter(d => d.deal_id !== dealId));
        setNdaDeals(prevDeals => prevDeals.filter(d => d.deal_id !== dealId));
        
        // Clear selection if the deleted deal was selected
        if (selectedDeal === dealId) {
          setSelectedDeal(null);
          setDealAccess([]);
        }
      }
    } catch (error) {
      setMessage(`Error deleting deal: ${error}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const confirmDeleteDeal = async () => {
    if (!deleteConfirm) return;

    // Find the deal to check access type
    const deal = deals.find(d => d.deal_id === deleteConfirm.dealId);
    const isReadOnlyUser = deal?.access_type === 'read';

    setIsLoading(true);
    try {
      if (isReadOnlyUser) {
        // For read-only users, just remove the deal from their view
        setDeals(prevDeals => prevDeals.filter(d => d.deal_id !== deleteConfirm.dealId));
        setMessage('Deal removed from your list');
      } else {
        // For users with write/admin access, actually delete the deal
        const response = await DatabaseService.deleteDeal(deleteConfirm.dealId, user?.id);
        
        if (response.error) {
          setMessage(`Error deleting deal: ${response.error}`);
        } else {
          setMessage('Deal deleted successfully');
          
          // Refresh deals list
          await loadDeals();
          await refreshDeals();
        }
      }
      
      setDeleteConfirm(null);
      
      // Clear selection if the deleted deal was selected
      if (selectedDeal === deleteConfirm.dealId) {
        setSelectedDeal(null);
        setDealAccess([]);
        
        // If we're on a deal-specific route, redirect to deals page
        if (location.pathname.includes(`/deals/${deleteConfirm.dealId}/`)) {
          navigate('/deals', { replace: true });
        }
      }
    } catch (error) {
      setMessage(`Error deleting deal: ${error}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const cancelDeleteDeal = () => {
    setDeleteConfirm(null);
  };

  const handleShareDeal = (deal: UserDeal) => {
    setShareModal({ deal, email: '', isLoading: false });
  };

  const handleSendInvitation = async () => {
    if (!shareModal || !shareModal.email.trim()) {
      setMessage('Please enter an email address');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    if (!user) {
      setMessage('User not authenticated');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shareModal.email)) {
      setMessage('Please enter a valid email address');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setShareModal({ ...shareModal, isLoading: true });
    
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/send-deal-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email: shareModal.email,
          deal_id: shareModal.deal.deal_id,
          deal_title: shareModal.deal.business_name,
          inviter_name: user.fullName || user.firstName || 'A user',
          inviter_email: user.emailAddresses[0]?.emailAddress || '',
          provider: 'mailersend'
        })
      });

      const result = await response.json();

      if (result.success) {
        setMessage(`✅ Invitation sent to ${shareModal.email}`);
        setShareModal(null);
      } else {
        throw new Error(result.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      setMessage(`❌ Failed to send invitation: ${error}`);
    } finally {
      if (shareModal) {
        setShareModal({ ...shareModal, isLoading: false });
      }
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const handleSendNDA = (deal: UserDeal) => {
    setNdaModal({ deal, email: '', isLoading: false });
  };

  const handleSendNDAInvitation = async () => {
    if (!ndaModal || !ndaModal.email.trim()) {
      setMessage('Please enter an email address');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    if (!user) {
      setMessage('User not authenticated');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(ndaModal.email)) {
      setMessage('Please enter a valid email address');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setNdaModal({ ...ndaModal, isLoading: true });
    
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/send-nda-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email: ndaModal.email,
          deal_id: ndaModal.deal.deal_id,
          deal_title: ndaModal.deal.business_name,
          inviter_name: user.fullName || user.firstName || 'A user',
          inviter_email: user.emailAddresses[0]?.emailAddress || '',
          provider: 'mailersend'
        })
      });

      const result = await response.json();

      if (result.success) {
        setMessage(`✅ NDA invitation sent to ${ndaModal.email}`);
        setNdaModal(null); // Close the modal
        setTimeout(() => setMessage(''), 5000);
      } else {
        throw new Error(result.message || 'Failed to send NDA invitation');
      }
    } catch (error) {
      console.error('Error sending NDA invitation:', error);
      setMessage(`❌ Failed to send NDA invitation: ${error}`);
      // Reset loading state but keep modal open for retry
      setNdaModal({ ...ndaModal, isLoading: false });
      setTimeout(() => setMessage(''), 5000);
    }
  };


  // Check if user has signed NDA for a deal - now using deal's NDA data directly
  const hasSignedNda = (deal: UserDeal) => {
    return deal.nda_signed === true;
  };


  const handleViewNDA = (dealId: string) => {
    // Validate deal ID before making request
    if (!dealId || dealId === 'undefined' || dealId === 'null' || dealId === null || dealId === undefined) {
      setMessage('❌ Invalid deal ID');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Additional validation for string "None"
    if (dealId === 'None' || dealId === 'none') {
      setMessage('❌ Deal ID is not available');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    try {
      // Open the NDA page in a new tab
      const ndaUrl = `/nda?deal_id=${dealId}&view_only=true`;
      window.open(ndaUrl, '_blank');
      setMessage('✅ Opening NDA page...');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error opening NDA page:', error);
      setMessage(`❌ Failed to open NDA page: ${error}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const handleEditDeal = (deal: UserDeal) => {
    setEditingDeal(deal);
    setEditDeal({
      business_name: deal.business_name,
      description: deal.description || '',
      deal_value: deal.deal_value,
      currency: deal.currency || 'USD'
    });
  };

  const handleUpdateDeal = async () => {
    if (!editingDeal || !editDeal.business_name) {
      setMessage('Please enter a business name');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    if (!user) {
      setMessage('User not authenticated');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setIsLoading(true);
    try {
      const response = await DatabaseService.updateDeal({
        id: editingDeal.deal_id,
        business_name: editDeal.business_name,
        business_alias: editingDeal.business_alias, // Preserve existing business_alias
        description: editDeal.description,
        deal_value: editDeal.deal_value,
        currency: editDeal.currency
      }, user.id);
      
      if (response.error) {
        setMessage(`Error updating deal: ${response.error}`);
      } else {
        setMessage('Deal updated successfully');
        setEditingDeal(null);
        setEditDeal({ business_name: '', description: '', deal_value: undefined, currency: 'USD' });
        
        // Refresh deals list
        await loadDeals();
        await refreshDeals();
      }
    } catch (error) {
      setMessage(`Error updating deal: ${error}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const cancelEditDeal = () => {
    setEditingDeal(null);
    setEditDeal({ business_name: '', description: '', deal_value: undefined, currency: 'USD' });
  };

  return (
    <div className="py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-purple-600" />
            {isCreateMode ? 'Create New Deal' : 'Deal Management'}
          </h2>
          {!isCreateMode && (
            <button
              onClick={() => navigate('/deals/archived')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Archive className="w-4 h-4" />
              View Archived Deals
            </button>
          )}
        </div>
        <p className="text-gray-600">
          {isCreateMode 
            ? 'Upload documents and configure deal details to get started with your business acquisition analysis.'
            : 'Create and manage business acquisition deals. Control access to specific deals for different users.'
          }
        </p>
      </div>

      {message && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{message}</p>
          </div>
        </div>
      )}

      {/* Create Deal Button - only show in management mode */}
      {!isCreateMode && (
        <div className="mb-6">
          <button
            onClick={() => navigate('/deals/create')}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create New Deal
          </button>
        </div>
      )}

      {/* Create Deal Form */}
      {(showCreateForm || isCreateMode) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Deal</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Name *
              </label>
              <input
                type="text"
                value={newDeal.business_name}
                onChange={(e) => setNewDeal({ ...newDeal, business_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., Advanced HVAC of ADA"
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deal Value
              </label>
              <input
                type="number"
                value={newDeal.deal_value || ''}
                onChange={(e) => setNewDeal({ ...newDeal, deal_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="1800000"
                disabled={isLoading}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={newDeal.description || ''}
                onChange={(e) => setNewDeal({ ...newDeal, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                rows={3}
                placeholder="Brief description of the deal..."
                disabled={isLoading}
              />
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button
                onClick={handleCreateDeal}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Create Deal
              </button>
              <button
                onClick={() => isCreateMode ? navigate('/deals') : setShowCreateForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Deal Form */}
      {editingDeal && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Deal: {editingDeal.business_name}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Name *
              </label>
              <input
                type="text"
                value={editDeal.business_name}
                onChange={(e) => setEditDeal({ ...editDeal, business_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., Advanced HVAC of ADA"
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deal Value
              </label>
              <input
                type="number"
                value={editDeal.deal_value || ''}
                onChange={(e) => setEditDeal({ ...editDeal, deal_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="1800000"
                disabled={isLoading}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={editDeal.description || ''}
                onChange={(e) => setEditDeal({ ...editDeal, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                rows={3}
                placeholder="Brief description of the deal..."
                disabled={isLoading}
              />
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button
                onClick={handleUpdateDeal}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Edit className="w-4 h-4" />
                )}
                Update Deal
              </button>
              <button
                onClick={cancelEditDeal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deals List - only show in management mode */}
      {!isCreateMode && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Your Deals
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={loadDeals}
                disabled={isLoading}
                className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => {
                  loadDeals();
                }}
                className="flex items-center gap-2 px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh NDA
              </button>
            </div>
          </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            <span className="ml-2 text-gray-600">Loading deals...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {/* NDA Deals */}
            {ndaDeals.map((ndaDeal) => (
              <div
                key={`nda-${ndaDeal.deal_id}`}
                className="border-2 border-orange-200 bg-orange-50 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">{ndaDeal.public_business_alias}</h4>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        NDA Required
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-2">
                      You've been invited to review confidential information for this business acquisition.
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Invited by: {ndaDeal.inviter_email}</span>
                      <span>Deal: {ndaDeal.public_business_alias}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSignNda(ndaDeal)}
                      className="flex items-center gap-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                      title="Sign NDA"
                    >
                      <Shield className="w-4 h-4" />
                      Sign NDA
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Regular Deals */}
            {deals.map((deal) => (
              <div
                key={deal.deal_id}
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedDeal === deal.deal_id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => handleDealSelect(deal)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {(() => {
                          // Check if user has signed NDA for this deal
                          const hasNda = hasSignedNda(deal);
                          
                          // For read-only users, show business_name if NDA is signed, otherwise business_alias
                          if (deal.access_type === 'read') {
                            if (hasNda) {
                              return deal.business_name || 'Business Opportunity';
                            } else {
                              // Use business_alias (guaranteed to exist due to database constraints)
                              return deal.business_alias;
                            }
                          }
                          // For admin/write users, always show business_name
                          return deal.business_name || 'Business Opportunity';
                        })()}
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDealStatusColor(deal.status)}`}>
                        {getDealStatusLabel(deal.status)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDealAccessTypeColor(deal.access_type)}`}>
                        {getDealAccessTypeLabel(deal.access_type)}
                      </span>
                    </div>
                    {deal.description && (
                      <p className="text-gray-600 text-sm mb-2">{deal.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      {deal.deal_value && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {formatCurrency(deal.deal_value, deal.currency)}
                        </span>
                      )}
                      <span>Created: {new Date(deal.created_at).toLocaleDateString()}</span>
                      <span>Updated: {new Date(deal.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDealSelect(deal);
                      }}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      title="Select Deal"
                    >
                      <ArrowRight className="w-3 h-3" />
                      Select
                    </button>
                    
                    {/* NDA View button - show for all users if NDA exists */}
                    {(() => {
                      // Early return if deal ID is invalid
                      if (!deal.deal_id || deal.deal_id === 'undefined' || deal.deal_id === 'null' || deal.deal_id === null || deal.deal_id === undefined || deal.deal_id === 'None' || deal.deal_id === 'none') {
                        return null;
                      }
                      
                      const hasNda = hasSignedNda(deal);
                      
                      if (hasNda) {
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewNDA(deal.deal_id);
                            }}
                            className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                            title="View Executed NDA"
                          >
                            <FileCheck className="w-4 h-4" />
                          </button>
                        );
                      } else if (isLoading) {
                        return (
                          <div className="p-2 text-gray-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                          </div>
                        );
                      } else if (deal.access_type === 'read' && !deal.nda_signed) {
                        // Show Sign NDA button only for users with read-only access who haven't signed the NDA yet
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Navigate to NDA page for signing
                              const ndaUrl = `/nda?deal_id=${deal.deal_id}&email=${user?.emailAddresses?.[0]?.emailAddress}&recipient=true`;
                              window.location.href = ndaUrl;
                            }}
                            className="flex items-center gap-1 px-3 py-1 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                            title="Sign NDA Required"
                          >
                            <Shield className="w-3 h-3" />
                            Sign NDA
                          </button>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Only show management buttons for users with write/admin access */}
                    {deal.access_type !== 'read' && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShareDeal(deal);
                          }}
                          className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Share Deal"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendNDA(deal);
                          }}
                          className="p-2 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-lg transition-colors"
                          title="Send NDA"
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectDeal(deal.deal_id);
                          }}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Manage Access"
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditDeal(deal);
                          }}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
                          title="Edit Deal"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDealDeletion(deal);
                          }}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete or Archive Deal"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {deals.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 mx-auto mb-6 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No deals yet</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Create your first deal to start managing business acquisitions. You can upload documents and configure deal details.
                </p>
                <button
                  onClick={() => navigate('/deals/create')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  <Plus className="w-5 h-5" />
                  Create Your First Deal
                </button>
              </div>
            )}
          </div>
        )}
        </div>
      )}

      {/* Document Upload Section for Create Mode */}
      {isCreateMode && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Upload Deal Documents
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Upload financial documents, tax returns, bank statements, and other relevant files for this deal.
          </p>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 mb-2">Drag and drop files here, or click to browse</p>
            <p className="text-sm text-gray-500">Supported formats: PDF, Excel, CSV, Word documents</p>
            <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Choose Files
            </button>
          </div>
        </div>
      )}

      {/* Deal Access Management - only show in management mode */}
      {selectedDeal && !isCreateMode && (
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Deal Access Management
          </h3>
          
          {isLoadingAccess ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading access information...</span>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Manage user access to this deal. Users with access can view and interact with deal-specific data.
              </p>
              
              {dealAccess.length > 0 ? (
                <div className="space-y-3">
                  {dealAccess.map((access) => (
                    <div key={access.id} className={`flex items-center justify-between p-3 rounded-lg ${access.is_owner ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">
                              {access.user_email || (access.user_id ? 'User ID: ' + access.user_id : 'Role: ' + access.role)}
                            </p>
                            {access.is_owner && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Owner
                              </span>
                            )}
                          </div>
                          {access.user_first_name && access.user_last_name && (
                            <p className="text-sm text-gray-600">
                              {access.user_first_name} {access.user_last_name}
                            </p>
                          )}
                          <p className="text-sm text-gray-500">
                            Granted: {new Date(access.granted_at).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDealAccessTypeColor(access.access_type)}`}>
                          {getDealAccessTypeLabel(access.access_type)}
                        </span>
                      </div>
                      {!access.is_owner && (
                        <button
                          onClick={() => access.user_id && handleRevokeAccess(selectedDeal, access.user_id)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          title="Revoke Access"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>No additional access granted. Only the deal owner has access.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (() => {
        const deal = deals.find(d => d.deal_id === deleteConfirm.dealId);
        const isReadOnlyUser = deal?.access_type === 'read';
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {isReadOnlyUser ? 'Remove Deal' : 'Delete Deal'}
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                {isReadOnlyUser ? (
                  <>
                    Are you sure you want to remove the deal <strong>"{deleteConfirm.dealBusinessName}"</strong> from your list? 
                    This will only remove it from your view and won't affect other users.
                  </>
                ) : (
                  <>
                    Are you sure you want to delete the deal <strong>"{deleteConfirm.dealBusinessName}"</strong>? 
                    This action cannot be undone and will remove all associated data.
                  </>
                )}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={cancelDeleteDeal}
                  disabled={isLoading}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteDeal}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {isReadOnlyUser ? 'Remove Deal' : 'Delete Deal'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Share Deal Modal */}
      {shareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Share2 className="w-5 h-5 text-purple-600" />
                Share Deal
              </h3>
              <button
                onClick={() => setShareModal(null)}
                disabled={shareModal.isLoading}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Share <strong>"{shareModal.deal.business_name}"</strong> with a new user:
              </p>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={shareModal.email}
                  onChange={(e) => setShareModal({ ...shareModal, email: e.target.value })}
                  placeholder="Enter email address"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={shareModal.isLoading}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                The recipient will receive an email invitation with a link to sign up and access this deal.
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShareModal(null)}
                disabled={shareModal.isLoading}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvitation}
                disabled={shareModal.isLoading || !shareModal.email.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {shareModal.isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4" />
                )}
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NDA Modal */}
      {ndaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-600" />
                Send NDA
              </h3>
              <button
                onClick={() => setNdaModal(null)}
                disabled={ndaModal.isLoading}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Send NDA for <strong>"{ndaModal.deal.business_name}"</strong> to:
              </p>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={ndaModal.email}
                  onChange={(e) => setNdaModal({ ...ndaModal, email: e.target.value })}
                  placeholder="Enter email address"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  disabled={ndaModal.isLoading}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                The recipient will receive an email with a link to sign the NDA and access deal information.
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setNdaModal(null)}
                disabled={ndaModal.isLoading}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendNDAInvitation}
                disabled={ndaModal.isLoading || !ndaModal.email.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {ndaModal.isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Shield className="w-4 h-4" />
                )}
                Send NDA
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deal Deletion Modal */}
      {deletionModal && (
        <DealDeletionModal
          isOpen={!!deletionModal}
          onClose={() => setDeletionModal(null)}
          deal={{
            id: deletionModal.deal.deal_id,
            business_name: deletionModal.deal.business_name || 'Business Opportunity',
            business_alias: deletionModal.deal.business_alias,
            description: deletionModal.deal.description,
            deal_value: deletionModal.deal.deal_value,
            currency: deletionModal.deal.currency || 'USD',
            created_at: deletionModal.deal.created_at
          }}
          onDelete={handlePermanentDelete}
          onArchive={handleArchiveDeal}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
